import { cn, Icon } from '@atlas/ui-primitives'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { HTMLAttributes, TdHTMLAttributes, ThHTMLAttributes } from 'react'

export enum SortDirection {
	ASC = 'asc',
	DESC = 'desc',
}

export interface TableProps extends HTMLAttributes<HTMLTableElement> {}

/**
 * The design system's tabular-data kit: thin, token-styled wrappers over the
 * native table elements, composed the same way as raw markup. Subcomponent
 * names deliberately match the app-local shadcn kit so its consumers can
 * migrate by import swap. Wide tables scroll inside their own wrapper — the
 * page never scrolls horizontally for a table.
 */
export function Table({ className, ...props }: TableProps) {
	return (
		<div className="w-full overflow-x-auto">
			<table className={cn('w-full caption-bottom', className)} {...props} />
		</div>
	)
}

export function TableHeader({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
	// Full-opacity border on the header row, over the rows' subdued /50 base.
	return <thead className={cn('[&_tr]:border-ds-border', className)} {...props} />
}

export function TableBody({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
	return <tbody className={cn('[&_tr:last-child]:border-b-0', className)} {...props} />
}

export function TableFooter({ className, ...props }: HTMLAttributes<HTMLTableSectionElement>) {
	return (
		<tfoot
			className={cn('border-t border-ds-border bg-ds-surface/50 font-medium [&_tr:last-child]:border-b-0', className)}
			{...props}
		/>
	)
}

export function TableRow({ className, ...props }: HTMLAttributes<HTMLTableRowElement>) {
	// Border lives on the row (not a section selector) so a per-row override
	// like `border-ds-border` can win through the class merge; the selected
	// state uses the kit's data attribute so migrating consumers keep working.
	return (
		<tr
			className={cn(
				'border-b border-ds-border/50 transition-colors hover:bg-ds-surface/50 data-[state=selected]:bg-ds-surface',
				className,
			)}
			{...props}
		/>
	)
}

export interface TableHeadProps extends ThHTMLAttributes<HTMLTableCellElement> {
	/** Direction of the active sort — set only on the actively sorted column. */
	sortDirection?: SortDirection | null
	/** Makes the header sortable: children render inside a button that requests the sort. Sort state stays with the caller. */
	onSort?: () => void
}

export function TableHead({ className, sortDirection = null, onSort, children, ...props }: TableHeadProps) {
	const ariaSort = sortDirection === SortDirection.ASC ? 'ascending' : sortDirection ? 'descending' : undefined
	return (
		<th
			aria-sort={onSort ? ariaSort : undefined}
			className={cn('px-ds-3 py-ds-2 text-left text-ds-xs uppercase text-ds-text-muted', className)}
			{...props}
		>
			{onSort ? (
				<button
					type="button"
					onClick={onSort}
					className="inline-flex items-center gap-ds-1 uppercase hover:text-ds-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ds-ring"
				>
					{children}
					{sortDirection ? (
						<Icon size="sm">{sortDirection === SortDirection.ASC ? <ChevronUp /> : <ChevronDown />}</Icon>
					) : null}
				</button>
			) : (
				children
			)}
		</th>
	)
}

export function TableCell({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
	return <td className={cn('px-ds-3 py-ds-2 align-middle', className)} {...props} />
}

export function TableCaption({ className, ...props }: HTMLAttributes<HTMLTableCaptionElement>) {
	return <caption className={cn('mt-ds-4 text-ds-sm leading-5 text-ds-text-muted', className)} {...props} />
}
